/**
 * Pure Stone (Pagar.me Core API v5) helpers: routing, money, payloads, webhook
 * mapping. No I/O — used by the provider, checkout routing, and unit tests.
 *
 * Stone charges the card once for the whole year, split by the issuer into 12
 * interest-free installments (we absorb the fee). There is one charge per year,
 * not one per installment, so the year is a single payment everywhere in zenpay.
 */
import type { BillingInterval, Currency, PaymentMethod } from "./types.js";

export const STONE_INSTALLMENT_COUNT = 12;

/**
 * Public checkout: annual + card + BRL goes to Stone. Monthly card and USD
 * annual stay on Stripe. Stone needs a CPF on the customer, so a buyer without
 * one stays on Stripe.
 */
export function shouldRouteAnnualCardToStone(args: {
  interval: BillingInterval;
  currency: Currency;
  paymentMethod: PaymentMethod;
  hasCpf?: boolean;
}): boolean {
  if (args.interval !== "annual") return false;
  if (args.currency !== "BRL") return false;
  if (args.hasCpf === false) return false;
  return args.paymentMethod === "stone" || args.paymentMethod === "stripe";
}

/** The client still sends `stripe` | `openpix` for the method; the server picks the provider. */
export function resolveCheckoutPaymentMethod(args: {
  requested: "stripe" | "openpix";
  interval: BillingInterval;
  currency: Currency;
  hasCpf?: boolean;
}): PaymentMethod {
  if (args.requested === "openpix") return "openpix";
  if (
    shouldRouteAnnualCardToStone({
      interval: args.interval,
      currency: args.currency,
      paymentMethod: "stripe",
      hasCpf: args.hasCpf,
    })
  ) {
    return "stone";
  }
  return "stripe";
}

/**
 * Display amount of one installment. When the total does not split evenly the
 * issuer puts the remainder cents on one installment, so round up: the label
 * never promises less than the buyer pays.
 */
export function stoneInstallmentCents(totalCents: number): number {
  return Math.ceil(Math.round(totalCents) / STONE_INSTALLMENT_COUNT);
}

/** Below R$ 1,00 per installment Stone refuses the split; such a total stays on Stripe. */
export const STONE_MIN_TOTAL_CENTS = STONE_INSTALLMENT_COUNT * 100;

export function isStoneEligibleAmount(totalCents: number): boolean {
  return Number.isInteger(totalCents) && totalCents >= STONE_MIN_TOTAL_CENTS;
}

/**
 * Whether a Pagar.me charge took money and then gave it back. A refunded card
 * charge is `canceled` in v5 (only its last transaction says `refunded`), so
 * `canceled` counts when the charge had been paid; a charge canceled before
 * payment (a declined first charge) did not take money.
 */
export function isReversedStoneCharge(charge: Record<string, unknown>): boolean {
  const status = typeof charge.status === "string" ? charge.status : "";
  if (status === "refunded" || status === "chargedback") return true;
  if (status !== "canceled") return false;
  const paidAmount = typeof charge.paid_amount === "number" ? charge.paid_amount : 0;
  return paidAmount > 0 || typeof charge.paid_at === "string";
}

export function digitsOnly(value: string): string {
  return value.replace(/\D/g, "");
}

/** Pagar.me wants phones split: country, area, number. Input is E.164 ("+5511999999999"). */
export function stonePhone(e164: string): { country_code: string; area_code: string; number: string } | null {
  const digits = digitsOnly(e164);
  if (!digits.startsWith("55") || digits.length < 12 || digits.length > 13) return null;
  return { country_code: "55", area_code: digits.slice(2, 4), number: digits.slice(4) };
}

/**
 * Whether a subscription can switch to the annual plan in 12x on Stone (a monthly subscriber on PIX
 * or Stripe card, paying in BRL, with nothing scheduled). Everyone else keeps the paths they had:
 * annual and Stone go through support, a scheduled change locks the page.
 */
export function canChangeToStoneAnnual(sub: {
  interval: BillingInterval;
  paymentMethod: PaymentMethod;
  status: string;
  currency: Currency;
  hasOpenScheduledChange: boolean;
}): boolean {
  return (
    sub.interval === "monthly" &&
    (sub.paymentMethod === "stripe" || sub.paymentMethod === "openpix") &&
    sub.status === "active" &&
    sub.currency === "BRL" &&
    !sub.hasOpenScheduledChange
  );
}

export interface StoneSubscriptionPayload {
  code: string;
  customer_id: string;
  payment_method: "credit_card";
  card_token: string;
  installments: number;
  interval: "year";
  interval_count: 1;
  billing_type: "prepaid";
  currency: "BRL";
  statement_descriptor: string;
  items: Array<{
    description: string;
    quantity: 1;
    pricing_scheme: { scheme_type: "unit"; price: number };
  }>;
  /** A plan change's credit: off the first cycle only, so the renewal charges the full price. */
  discounts?: Array<{ cycles: 1; discount_type: "flat"; value: number }>;
  metadata: Record<string, string>;
}

/**
 * Yearly subscription charged on creation, 12x on the card, renewed yearly on
 * the saved card. The price is what the buyer pays today (coupon and add-ons
 * included), the same rule as the Stripe subscription a new customer gets.
 *
 * A plan change (`recurringCents` above `amountCents`) is priced at the full
 * recurring amount with a flat discount on the first cycle only: the first
 * year charges `amountCents` (the annual less the unused credit) and every
 * renewal the full amount, as the Stripe change does with a duration-once coupon.
 */
export function buildStoneSubscriptionPayload(args: {
  orderId: string;
  customerId: string;
  cardToken: string;
  amountCents: number;
  /** Plan change only: the full amount every renewal charges. */
  recurringCents?: number;
  description: string;
  metadata?: Record<string, string>;
}): StoneSubscriptionPayload {
  if (!isStoneEligibleAmount(args.amountCents)) {
    throw new Error("stone_amount_too_small_for_installments");
  }
  const recurringCents = args.recurringCents ?? args.amountCents;
  if (!Number.isInteger(recurringCents) || recurringCents < args.amountCents) {
    throw new Error("stone_recurring_below_first_charge");
  }
  const firstCycleDiscountCents = recurringCents - args.amountCents;
  return {
    code: args.orderId,
    customer_id: args.customerId,
    payment_method: "credit_card",
    card_token: args.cardToken,
    installments: STONE_INSTALLMENT_COUNT,
    interval: "year",
    interval_count: 1,
    billing_type: "prepaid",
    currency: "BRL",
    statement_descriptor: "ZENPOST",
    items: [
      {
        description: args.description,
        quantity: 1,
        pricing_scheme: { scheme_type: "unit", price: recurringCents },
      },
    ],
    ...(firstCycleDiscountCents > 0
      ? { discounts: [{ cycles: 1 as const, discount_type: "flat" as const, value: firstCycleDiscountCents }] }
      : {}),
    metadata: { zenpay_order_id: args.orderId, ...(args.metadata ?? {}) },
  };
}

export type InternalOrderStatus = "paid" | "pending" | "failed" | "expired" | "refunded" | "canceled";

/** Pagar.me charge status → zenpay order status. */
export function mapStoneChargeStatus(status: string | null | undefined): InternalOrderStatus {
  switch ((status ?? "").toLowerCase()) {
    case "paid":
      return "paid";
    case "failed":
      return "failed";
    case "refunded":
    case "chargedback":
      return "refunded";
    case "canceled":
      return "canceled";
    default:
      // pending, processing, underpaid, overpaid
      return "pending";
  }
}

export interface ParsedStoneWebhook {
  providerEventId: string;
  eventType: string;
  subscriptionId?: string;
  invoiceId?: string;
  chargeId?: string;
  customerId?: string;
  /** zenpay order id, from the subscription `code` / metadata set at checkout. */
  zenpayOrderId?: string;
  /** Billing cycle the event is about; 1 is the checkout's own charge. */
  cycle?: number;
  amountCents?: number;
  orderStatus?: InternalOrderStatus;
  isRenewal: boolean;
  refundedAt?: Date;
  failure?: {
    reason: string;
    message?: string;
    attempt?: number;
    nextRetryAt?: string | null;
    isFinalAttempt: boolean;
  };
}

function rec(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

function str(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

function num(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() && Number.isFinite(Number(value))) return Number(value);
  return undefined;
}

function date(value: unknown): Date | undefined {
  const s = str(value);
  if (!s) return undefined;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? undefined : d;
}

/**
 * Map a Pagar.me webhook (`{ id, type, created_at, data }`) to what zenpay acts on.
 *
 * - `invoice.paid` on cycle 1 completes the checkout; on cycle ≥ 2 it is a renewal.
 * - `invoice.payment_failed` on cycle ≥ 2 is a failed renewal (Pagar.me retries it).
 *   On cycle 1 the checkout already answered the buyer synchronously.
 * - `charge.refunded` / `charge.chargedback` / `chargeback.received` reverse the
 *   money → refund path. `chargeback.received` replaces `charge.chargedback`
 *   (Pagar.me is discontinuing the old one); both are handled.
 *
 * The event `id` (`hook_…`) is unique per delivery of a distinct event, so it is
 * the idempotency key. Never key on the subscription id: it repeats every year.
 */
export function parseStoneWebhookPayload(body: Record<string, unknown>): ParsedStoneWebhook {
  const eventType = str(body.type) ?? "unknown";
  const data = rec(body.data);
  const providerEventId = str(body.id) ?? `stone_${eventType}_${str(data.id) ?? Date.now()}`;

  const isInvoice = eventType.startsWith("invoice.");
  const isCharge = eventType.startsWith("charge.");
  const isChargeback = eventType.startsWith("chargeback.");
  const isSubscription = eventType.startsWith("subscription.");

  // A chargeback event carries the disputed charge inside it.
  const chargebackCharge = isChargeback ? rec(data.charge) : {};
  const invoice = isInvoice ? data : rec(isChargeback ? chargebackCharge.invoice : data.invoice);
  const charge = isCharge ? data : isChargeback ? chargebackCharge : rec(invoice.charge);
  const subscription = isSubscription ? data : rec(invoice.subscription);

  const subscriptionId =
    str(subscription.id) ?? str(invoice.subscription_id) ?? str(invoice.subscriptionId) ?? str(data.subscription_id);
  const invoiceId = str(invoice.id);
  const chargeId = str(charge.id);
  const customerId = str(rec(data.customer).id) ?? str(data.customer_id) ?? str(rec(invoice.customer).id);
  const zenpayOrderId =
    str(subscription.code) ??
    str(rec(subscription.metadata).zenpay_order_id) ??
    str(rec(invoice.metadata).zenpay_order_id) ??
    str(rec(charge.metadata).zenpay_order_id);
  const cycleObj = rec(invoice.cycle);
  const cycle = num(cycleObj.cycle) ?? num(invoice.cycle_number);
  const amountCents = num(charge.paid_amount) ?? num(charge.amount) ?? num(invoice.amount);

  let orderStatus: InternalOrderStatus | undefined;
  let isRenewal = false;
  let refundedAt: Date | undefined;
  let failure: ParsedStoneWebhook["failure"];

  switch (eventType) {
    case "invoice.paid":
      orderStatus = "paid";
      isRenewal = cycle !== undefined && cycle >= 2;
      break;
    case "invoice.payment_failed": {
      orderStatus = "failed";
      isRenewal = cycle !== undefined && cycle >= 2;
      const tx = rec(charge.last_transaction);
      const gateway = rec(tx.gateway_response);
      const errors = Array.isArray(gateway.errors) ? (gateway.errors as unknown[]).map(rec) : [];
      // v5 keeps the retry bookkeeping on the transaction (attempt_count,
      // max_attempts, next_attempt). A failure is the last one only when those
      // say so: without them, calling every failure final would send Zenpost a
      // "last attempt" notice on each retry.
      const attempt = num(tx.attempt_count) ?? num(invoice.attempts) ?? num(charge.attempts);
      const maxAttempts = num(tx.max_attempts);
      const nextAttempt = str(tx.next_attempt) ?? str(charge.next_attempt) ?? str(invoice.next_attempt) ?? null;
      failure = {
        reason: (str(tx.acquirer_return_code) ?? str(gateway.code) ?? "payment_failed").slice(0, 80),
        message: (str(tx.acquirer_message) ?? str(errors[0]?.message))?.slice(0, 500),
        attempt,
        nextRetryAt: nextAttempt,
        // With the cap known, compare to it; without it, an attempt count and no
        // next attempt scheduled is the last one.
        isFinalAttempt:
          attempt !== undefined && (maxAttempts !== undefined ? attempt >= maxAttempts : nextAttempt === null),
      };
      break;
    }
    // charge.partial_canceled is not a reversal of the year: zenpay only ever
    // refunds in full, so a partial one was made by hand and access stays.
    case "charge.refunded":
    case "charge.chargedback":
    case "chargeback.received":
      orderStatus = "refunded";
      refundedAt = isChargeback
        ? (date(data.created_at) ?? date(body.created_at) ?? new Date())
        : (date(data.canceled_at) ?? date(data.updated_at) ?? date(body.created_at) ?? new Date());
      break;
    default:
      // subscription.canceled is not acted on: zenpay itself cancels Stone
      // subscriptions (scheduled cancellation, refund, chargeback) and keeps the
      // paid year locally. Marking the row canceled here would undo that.
      break;
  }

  return {
    providerEventId,
    eventType,
    subscriptionId,
    invoiceId,
    chargeId,
    customerId,
    zenpayOrderId,
    cycle,
    amountCents,
    orderStatus,
    isRenewal,
    refundedAt,
    failure,
  };
}
